<template>
  <FormGroup>
    <View direction="column" gap="sm">
      <Text variant="h1">Check your email</Text>
      <Text variant="body-large" color="secondary">We've sent you a six-digit confirmation code to</Text>
      <Text variant="label-large">{{ route.query.email }}</Text>
    </View>

    <FormField label="Enter 6-digit code" :error="form.fieldErrors('verificationCode')">
      <Input :disabled="form.submitting" type="number" v-model="form.fields.verificationCode">
        <template #suffix>
          <LoadingIndicator :name="'suffix'" class="loader" v-if="form.submitting" />
        </template>
      </Input>
    </FormField>

    <div v-if="!isCodeResent">
      <span>Don't receive the code?&nbsp;</span>
      <Link color="accent" @click="resendEmailConfirmationCode">Resend code</Link>
    </div>

    <div v-else>The code has been resent.</div>
  </FormGroup>
</template>

<script setup lang="ts">
import { maxLength, minLength, required } from '@vuelidate/validators';
import { ref, watch } from 'vue';
import { RouteLocationRaw, useRoute, useRouter } from 'vue-router';

import {
  extractErrorMessage,
  FormField,
  FormGroup,
  Input,
  Link,
  LoadingIndicator,
  Text,
  View,
  useForm,
} from '@tallo/design-system';

import { useAuthStore } from '../auth.store';

const route = useRoute();
const authStore = useAuthStore();
const router = useRouter();

const props = defineProps<{
  /**
   * Custom function to be executed when the verification code is submitted
   * @param code - The verification code to be verified
   * @description if not provided, the component will use the default "one time code" sign in verification function
   */
  customVerifyCodeRequest?: (code: number) => Promise<void>;
  /**
   * Custom function to be executed when the "Resend code" link is clicked
   * @description if not provided, the component will use the default "one time code" sign in resend code function
   */
  customResendCodeRequest?: () => Promise<void>;
  navigationAfterVerification?: RouteLocationRaw;
}>();

const renterEmail = route.query.email as string;
const isCodeResent = ref(false);

const form = useForm(
  {
    verificationCode: '',
  },
  {
    verificationCode: {
      required,
      minLength: minLength(6),
      maxLength: maxLength(6),
    },
  },
);

watch(
  () => form.fields.verificationCode,
  async () => {
    form.clearValidationErrors();

    if (!form.fields.verificationCode || form.fields.verificationCode.toString().length < 6) {
      return;
    }

    form.submit(async () => {
      const code = Number(form.fields.verificationCode);

      try {
        if (props.customVerifyCodeRequest) {
          await props.customVerifyCodeRequest(code);
        } else {
          await authStore.signInWithOneTimeCode(renterEmail as string, code);

          if (props.navigationAfterVerification) {
            router.push(props.navigationAfterVerification);
          }
        }
      } catch (error) {
        form.setFieldError('verificationCode', extractErrorMessage(error, 'Invalid Code'));
      }
    });
  },
  { immediate: true },
);

async function resendEmailConfirmationCode() {
  if (props.customResendCodeRequest) {
    await props.customResendCodeRequest();
  } else {
    await authStore.requestOneTimeSignInCode(renterEmail);
  }

  isCodeResent.value = true;

  setTimeout(() => (isCodeResent.value = false), 30_000);
}
</script>
